import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Automated Verification Service
 * Performs automated checks on influencer content submissions
 * Implements hybrid model: required checks (must pass) and advisory checks (warnings)
 * Integrates with content monitoring service and tracking link data
 */

const automatedVerificationService = {
  /**
   * Get content detection data for a campaign-influencer combination
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer ID
   * @returns {Promise<object|null>} - Content detection data or null
   */
  getContentDetection: async (campaignId, influencerId) => {
    try {
      const detectionsRef = collection(db, 'contentDetections');
      const q = query(
        detectionsRef,
        where('campaignId', '==', campaignId),
        where('influencerId', '==', influencerId),
        orderBy('detectedAt', 'desc'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting content detection:', error);
      return null;
    }
  },
  /**
   * Run all automated verification checks on a post URL
   * @param {string} postUrl - The social media post URL
   * @param {string} campaignId - Campaign ID for context
   * @param {string} influencerId - Influencer ID for tracking link lookup
   * @param {object} campaignRequirements - Campaign requirements (hashtags, etc.)
   * @param {string} trackingCode - Tracking code for engagement analytics (optional)
   * @returns {Promise<object>} Verification results with required and advisory checks
   */
  verifyContent: async (postUrl, campaignId, influencerId, campaignRequirements = {}, trackingCode = null) => {
    try {
      const results = {
        required: {},
        advisory: {},
        timestamp: new Date().toISOString()
      };

      // Get content detection data from monitoring service
      const contentDetection = await automatedVerificationService.getContentDetection(campaignId, influencerId);

      // Run all checks in parallel for better performance
      const [
        urlValidation,
        partnershipDisclosure,
        hashtagsCheck,
        trackingLinkCheck,
        brandMentionCheck,
        engagementCheck
      ] = await Promise.all([
        automatedVerificationService.checkPostUrlValid(postUrl),
        automatedVerificationService.checkPartnershipDisclosure(postUrl, contentDetection),
        automatedVerificationService.checkRequiredHashtags(postUrl, campaignRequirements.hashtags || [], contentDetection),
        automatedVerificationService.checkTrackingLinkClicks(campaignId, influencerId),
        automatedVerificationService.checkRequiredMentions(postUrl, campaignRequirements.requiredMentions || [], contentDetection),
        automatedVerificationService.checkEngagement(postUrl, influencerId, contentDetection, trackingCode)
      ]);

      // Required checks
      results.required.postUrlValid = urlValidation;
      results.required.partnershipDisclosed = partnershipDisclosure;
      results.required.requiredHashtags = hashtagsCheck;

      // Advisory checks
      results.advisory.trackingLinkClicks = trackingLinkCheck;
      results.advisory.requiredMentions = brandMentionCheck;
      results.advisory.engagement = engagementCheck;

      return results;
    } catch (error) {
      console.error('Error running automated verification:', error);
      throw error;
    }
  },

  /**
   * Check if post URL is valid and accessible
   * REQUIRED CHECK
   */
  checkPostUrlValid: async (postUrl) => {
    try {
      if (!postUrl || postUrl.trim() === '') {
        return {
          passed: false,
          message: 'Post URL is required',
          details: 'No URL provided'
        };
      }

      // Validate URL format
      let url;
      try {
        url = new URL(postUrl);
      } catch (e) {
        return {
          passed: false,
          message: 'Invalid URL format',
          details: 'URL is not properly formatted'
        };
      }

      // Check if it's a supported platform
      const supportedPlatforms = ['instagram.com', 'tiktok.com', 'facebook.com', 'fb.com'];
      const isSupported = supportedPlatforms.some(platform => url.hostname.includes(platform));

      if (!isSupported) {
        return {
          passed: false,
          message: 'Unsupported platform',
          details: 'URL must be from Instagram, TikTok, or Facebook'
        };
      }

      // Check URL accessibility with proper CORS handling
      try {
        // For social media platforms, we can't directly fetch due to CORS
        // Instead, we'll do basic validation and require manual verification for accessibility
        
        // Check for obvious invalid URLs (malformed paths, suspicious patterns)
        const pathname = url.pathname;
        
        // Basic validation for Instagram URLs
        if (url.hostname.includes('instagram.com')) {
          // Instagram post URLs should have /p/ or /reel/ in the path
          if (!pathname.includes('/p/') && !pathname.includes('/reel/')) {
            return {
              passed: false,
              message: 'Invalid Instagram URL format',
              details: 'Instagram post URLs should contain /p/ or /reel/ in the path',
              platform: 'Instagram'
            };
          }
        }
        
        // Basic validation for TikTok URLs
        if (url.hostname.includes('tiktok.com')) {
          // TikTok URLs should have a video ID or @username format
          if (!pathname.includes('/video/') && !pathname.includes('/@')) {
            return {
              passed: false,
              message: 'Invalid TikTok URL format',
              details: 'TikTok URLs should contain /video/ or /@username in the path',
              platform: 'TikTok'
            };
          }
        }
        
        // Basic validation for Facebook URLs
        if (url.hostname.includes('facebook.com') || url.hostname.includes('fb.com')) {
          // Facebook URLs can have various formats: /posts/, /photos/, /videos/, /permalink/, /share/p/, /share/v/
          if (!pathname.includes('/posts/') && !pathname.includes('/photos/') && 
              !pathname.includes('/videos/') && !pathname.includes('/permalink/') &&
              !pathname.includes('/share/p/') && !pathname.includes('/share/v/') &&
              !pathname.includes('/share/r/')) {
            return {
              passed: false,
              message: 'Invalid Facebook URL format',
              details: 'Facebook URLs should contain /posts/, /photos/, /videos/, /share/p/, or /share/v/ in the path',
              platform: 'Facebook'
            };
          }
        }
        
        // If URL passes basic validation, mark as valid but requiring manual verification
        // This prevents false negatives while still catching obviously invalid URLs
        return {
          passed: true,
          message: 'Post URL is valid and accessible',
          details: `Valid ${url.hostname} URL`,
          platform: url.hostname.includes('instagram') ? 'Instagram' : 
                   url.hostname.includes('tiktok') ? 'TikTok' : 'Facebook'
        };
        
      } catch (error) {
        return {
          passed: false,
          message: 'Error validating URL',
          details: `Error checking URL: ${error.message}`,
          platform: url.hostname.includes('instagram') ? 'Instagram' : 
                   url.hostname.includes('tiktok') ? 'TikTok' : 'Facebook'
        };
      }
    } catch (error) {
      console.error('Error checking post URL:', error);
      return {
        passed: false,
        message: 'Error validating URL',
        details: error.message
      };
    }
  },

  /**
   * Check if partnership is properly disclosed
   * REQUIRED CHECK
   */
  checkPartnershipDisclosure: async (postUrl, contentDetection = null) => {
    try {
      // Required disclosure keywords
      const disclosureKeywords = ['#ad', '#sponsored', '#partnership', '#collab', '#paidpartnership'];
      
      // If content monitoring detected this post, check the caption
      if (contentDetection && contentDetection.caption) {
        const caption = contentDetection.caption.toLowerCase();
        const hasDisclosure = disclosureKeywords.some(keyword => 
          caption.includes(keyword.toLowerCase())
        );
        
        if (hasDisclosure) {
          const foundKeyword = disclosureKeywords.find(keyword => 
            caption.includes(keyword.toLowerCase())
          );
          return {
            passed: true,
            message: `Partnership disclosure found (${foundKeyword})`,
            details: 'Legal disclosure requirement met via content monitoring',
            note: 'Verified via passive content monitoring'
          };
        } else {
          return {
            passed: false,
            message: 'Partnership disclosure not found in post',
            details: `Required: ${disclosureKeywords.join(', ')}`,
            note: 'Content monitored but no disclosure detected'
          };
        }
      }
      
      // If no content detection data, pass but require manual verification
      return {
        passed: true,
        message: 'Partnership disclosure requires manual brand verification',
        details: 'Content monitoring data not yet available. Brand will manually verify disclosure.',
        note: 'Manual verification required',
        requiresManualReview: true
      };
    } catch (error) {
      console.error('Error checking partnership disclosure:', error);
      return {
        passed: false,
        message: 'Could not verify partnership disclosure',
        details: error.message
      };
    }
  },

  /**
   * Check if required hashtags are present
   * REQUIRED CHECK
   */
  checkRequiredHashtags: async (postUrl, requiredHashtags = [], contentDetection = null) => {
    try {
      if (!requiredHashtags || requiredHashtags.length === 0) {
        return {
          passed: true,
          message: 'No specific hashtags required',
          details: 'Campaign has no mandatory hashtags'
        };
      }

      // If content monitoring detected this post, check the caption for hashtags
      if (contentDetection && contentDetection.caption) {
        const caption = contentDetection.caption.toLowerCase();
        const missingHashtags = [];
        const foundHashtags = [];
        
        requiredHashtags.forEach(hashtag => {
          const hashtagLower = hashtag.toLowerCase();
          // Check for hashtag with or without # symbol
          const hashtagVariants = [
            hashtagLower,
            hashtagLower.startsWith('#') ? hashtagLower : `#${hashtagLower}`
          ];
          
          const found = hashtagVariants.some(variant => caption.includes(variant));
          
          if (found) {
            foundHashtags.push(hashtag);
          } else {
            missingHashtags.push(hashtag);
          }
        });
        
        if (missingHashtags.length === 0) {
          return {
            passed: true,
            message: `All required hashtags present (${foundHashtags.length})`,
            details: foundHashtags.join(', '),
            note: 'Verified via passive content monitoring'
          };
        } else {
          return {
            passed: false,
            message: `Missing ${missingHashtags.length} required hashtag(s)`,
            details: `Missing: ${missingHashtags.join(', ')}`,
            note: 'Content monitored but some hashtags not found'
          };
        }
      }

      // If no content detection data, pass but require manual verification
      return {
        passed: true,
        message: `Required hashtags need manual brand verification (${requiredHashtags.length})`,
        details: `Content monitoring data not yet available. Brand will manually verify: ${requiredHashtags.join(', ')}`,
        note: 'Manual verification required',
        requiresManualReview: true
      };
    } catch (error) {
      console.error('Error checking hashtags:', error);
      return {
        passed: false,
        message: 'Could not verify hashtags',
        details: error.message
      };
    }
  },

  /**
   * Check if tracking link has clicks
   * ADVISORY CHECK
   */
  checkTrackingLinkClicks: async (campaignId, influencerId) => {
    try {
      // Query trackingLinks collection
      const trackingLinksRef = collection(db, 'trackingLinks');
      const q = query(
        trackingLinksRef,
        where('campaignId', '==', campaignId),
        where('influencerId', '==', influencerId)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return {
          passed: false,
          warning: true,
          message: 'No tracking link found',
          details: 'Tracking link may not have been generated yet',
          value: 0
        };
      }

      const trackingLink = querySnapshot.docs[0].data();
      const clickCount = trackingLink.analytics?.clicks || 0;

      if (clickCount === 0) {
        return {
          passed: false,
          warning: true,
          message: 'Tracking link has 0 clicks',
          details: 'Link may need more time to accumulate clicks',
          value: 0
        };
      }

      return {
        passed: true,
        warning: false,
        message: `Tracking link active (${clickCount} clicks)`,
        details: 'Link is being used and tracked',
        value: clickCount
      };
    } catch (error) {
      console.error('Error checking tracking link clicks:', error);
      return {
        passed: false,
        warning: true,
        message: 'Could not verify tracking link',
        details: error.message,
        value: 0
      };
    }
  },

  /**
   * Check if required mentions/tags are present
   * ADVISORY CHECK
   */
  checkRequiredMentions: async (postUrl, requiredMentions = [], contentDetection = null) => {
    try {
      if (!requiredMentions || requiredMentions.length === 0) {
        return {
          passed: true,
          warning: false,
          message: 'No specific mentions required',
          details: 'Campaign has no mandatory brand mentions'
        };
      }

      // If content monitoring detected this post, check for mentions
      if (contentDetection && contentDetection.caption) {
        const caption = contentDetection.caption.toLowerCase();
        const missingMentions = [];
        const foundMentions = [];
        
        requiredMentions.forEach(mention => {
          const mentionLower = mention.toLowerCase();
          // Check for mention with or without @ symbol
          const mentionVariants = [
            mentionLower,
            mentionLower.startsWith('@') ? mentionLower : `@${mentionLower}`
          ];
          
          const found = mentionVariants.some(variant => caption.includes(variant));
          
          if (found) {
            foundMentions.push(mention);
          } else {
            missingMentions.push(mention);
          }
        });
        
        if (missingMentions.length === 0) {
          return {
            passed: true,
            warning: false,
            message: `All required mentions present (${foundMentions.length})`,
            details: foundMentions.join(', '),
            note: 'Verified via passive content monitoring'
          };
        } else {
          return {
            passed: false,
            warning: true,
            message: `Missing ${missingMentions.length} required mention(s)`,
            details: `Missing: ${missingMentions.join(', ')}`,
            note: 'Advisory warning - mentions may be tagged in image'
          };
        }
      }

      // If no content detection data, this is advisory
      return {
        passed: false,
        warning: true,
        message: `Required mentions need manual verification (${requiredMentions.length})`,
        details: `Content monitoring data not yet available. Brand will manually verify: ${requiredMentions.join(', ')}`,
        note: 'Manual verification required'
      };
    } catch (error) {
      console.error('Error checking required mentions:', error);
      return {
        passed: false,
        warning: true,
        message: 'Could not verify required mentions',
        details: error.message
      };
    }
  },

  /**
   * Check if brand is mentioned/tagged (legacy function for backward compatibility)
   * ADVISORY CHECK
   */
  checkBrandMention: async (postUrl, brandHandle, contentDetection = null) => {
    try {
      if (!brandHandle || brandHandle.trim() === '') {
        return {
          passed: true,
          warning: false,
          message: 'Brand mention not required',
          details: 'No brand handle specified'
        };
      }

      // If content monitoring detected this post, check for brand mention
      if (contentDetection && contentDetection.caption) {
        const caption = contentDetection.caption.toLowerCase();
        const handleVariants = [
          brandHandle.toLowerCase(),
          brandHandle.toLowerCase().startsWith('@') ? brandHandle.toLowerCase() : `@${brandHandle.toLowerCase()}`
        ];
        
        const hasMention = handleVariants.some(variant => caption.includes(variant));
        
        if (hasMention) {
          return {
            passed: true,
            warning: false,
            message: 'Brand tagged or mentioned in post',
            details: `@${brandHandle} found in caption`,
            note: 'Verified via passive content monitoring'
          };
        } else {
          return {
            passed: false,
            warning: true,
            message: 'Brand mention not detected',
            details: `@${brandHandle} not found in caption`,
            note: 'Advisory warning - brand may be tagged in image'
          };
        }
      }

      // If no content detection data, this is advisory
      return {
        passed: false,
        warning: true,
        message: 'Brand mention needs manual verification',
        details: `Please verify that @${brandHandle} is tagged or mentioned`,
        note: 'Content monitoring pending - advisory check'
      };
    } catch (error) {
      console.error('Error checking brand mention:', error);
      return {
        passed: false,
        warning: true,
        message: 'Could not verify brand mention',
        details: error.message
      };
    }
  },

  /**
   * Check engagement level using real tracking link analytics
   * ADVISORY CHECK
   */
  checkEngagement: async (postUrl, influencerId, contentDetection = null, trackingCode = null) => {
    try {
      // First, try to get real tracking link analytics
      if (trackingCode) {
        const trackingLink = await automatedVerificationService.getTrackingLinkAnalytics(trackingCode);
        
        if (trackingLink && trackingLink.analytics) {
          const impressions = trackingLink.analytics.impressions || 0;
          const clicks = trackingLink.analytics.clicks || 0;
          const engagements = trackingLink.analytics.engagements || 0;
          
          // Calculate engagement metrics from tracking data
          const totalEngagement = clicks + engagements;
          let engagementRate = 0;
          if (impressions > 0) {
            engagementRate = ((totalEngagement / impressions) * 100).toFixed(2);
          }
          
          // Advisory thresholds (can be adjusted)
          const minImpressions = 10; // Minimum impression count
          const minEngagementRate = 1.0; // Minimum 1% engagement rate
          
          if (impressions >= minImpressions && (impressions === 0 || engagementRate >= minEngagementRate)) {
            return {
              passed: true,
              warning: false,
              message: `Good engagement: ${impressions} views, ${totalEngagement} interactions${impressions > 0 ? ` (${engagementRate}%)` : ''}`,
              details: `Clicks: ${clicks}, Engagements: ${engagements}`,
              value: parseFloat(engagementRate) || totalEngagement,
              note: 'Verified via tracking link analytics'
            };
          } else if (impressions > 0) {
            return {
              passed: false,
              warning: true,
              message: `Low engagement: ${impressions} views, ${totalEngagement} interactions${impressions > 0 ? ` (${engagementRate}%)` : ''}`,
              details: `Clicks: ${clicks}, Engagements: ${engagements}`,
              value: parseFloat(engagementRate) || totalEngagement,
              note: 'Advisory warning - engagement below threshold'
            };
          } else {
            return {
              passed: false,
              warning: true,
              message: 'No tracking data available yet',
              details: 'Tracking link may not have been accessed',
              value: 0,
              note: 'Check if tracking link is included in post'
            };
          }
        }
      }
      
      // Fallback: Check if content monitoring has real data (not simulated)
      if (contentDetection && contentDetection.engagement !== undefined && !contentDetection.simulated) {
        const engagement = contentDetection.engagement || 0;
        const reach = contentDetection.reach || 0;
        
        // Calculate engagement rate if we have reach data
        let engagementRate = 0;
        if (reach > 0) {
          engagementRate = ((engagement / reach) * 100).toFixed(2);
        }
        
        // Advisory thresholds (can be adjusted)
        const minEngagement = 50; // Minimum engagement count
        const minRate = 1.0; // Minimum 1% engagement rate
        
        if (engagement >= minEngagement && (reach === 0 || engagementRate >= minRate)) {
          return {
            passed: true,
            warning: false,
            message: `Good engagement: ${engagement} interactions${reach > 0 ? ` (${engagementRate}%)` : ''}`,
            details: reach > 0 ? `Reach: ${reach}` : 'Engagement tracked',
            value: engagementRate || engagement,
            note: 'Verified via content monitoring'
          };
        } else {
          return {
            passed: false,
            warning: true,
            message: `Low engagement: ${engagement} interactions${reach > 0 ? ` (${engagementRate}%)` : ''}`,
            details: reach > 0 ? `Reach: ${reach}` : 'Consider boosting post',
            value: engagementRate || engagement,
            note: 'Advisory warning - engagement below threshold'
          };
        }
      }
      
      // If no real data available, show appropriate message
      return {
        passed: false,
        warning: true,
        message: 'Engagement data not yet available',
        details: 'Post engagement will be tracked over time via tracking link',
        value: 0,
        note: 'Include tracking link in post for real-time analytics'
      };
    } catch (error) {
      console.error('Error checking engagement:', error);
      return {
        passed: false,
        warning: true,
        message: 'Could not verify engagement',
        details: error.message,
        value: 0
      };
    }
  },

  /**
   * Get tracking link analytics by tracking code
   */
  getTrackingLinkAnalytics: async (trackingCode) => {
    try {
      const trackingLinksRef = collection(db, 'trackingLinks');
      const q = query(
        trackingLinksRef,
        where('trackingCode', '==', trackingCode),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('Error getting tracking link analytics:', error);
      return null;
    }
  },

  /**
   * Get verification summary
   * Returns whether all required checks passed and advisory warnings
   */
  getVerificationSummary: (verificationResults) => {
    const requiredPassed = Object.values(verificationResults.required).every(check => check.passed);
    const advisoryWarnings = Object.values(verificationResults.advisory).filter(check => check.warning);

    return {
      allRequiredPassed: requiredPassed,
      requiredCheckCount: Object.keys(verificationResults.required).length,
      requiredPassedCount: Object.values(verificationResults.required).filter(c => c.passed).length,
      advisoryWarningCount: advisoryWarnings.length,
      canSubmit: requiredPassed,
      summary: requiredPassed 
        ? `All required checks passed${advisoryWarnings.length > 0 ? ` (${advisoryWarnings.length} advisory warnings)` : ''}`
        : 'Some required checks failed - cannot submit'
    };
  }
};

export default automatedVerificationService;
